import React, { useMemo } from "react";
import {
  Container,
  HotelCarousel,
  Title,
  AccordionDetails,
  Address,
  AboutTitle,
  Time,
  Phone,
  AmenityList,
} from "./HotelSummaryHelpers";
import { Col, Row } from "react-bootstrap";
import { IHotelSummaryProps } from "./HotelSummaryProps";
import { mobileMargin } from "./HotelSummary.module.scss";

// Static Assets
import fallbackImage from "../../../images/hotel_fallback.jpg";
import { useMediaQuery } from "../../../hooks/useMediaQuery";

const baseUrl = process.env.GATSBY_CORE_CONTENT_BASE_URL;

const HotelSummary = ({ hotel }: IHotelSummaryProps) => {
  const isMobile = useMediaQuery("(max-width: 991px)");
  const checkInTime = hotel.field_check_in || "3:00 PM";
  const checkOutTime = hotel.field_check_out || "11:00 AM";
  const locality = hotel?.address?.locality;

  let aboutText = "";
  let aboutTitle = "";
  const features = hotel.features;
  if (features && features.length) {
    const about = features.filter((f: any) => {
      const tabKey = f.tab_title?.toLowerCase().trim();
      return tabKey === "about";
    });
    aboutText = about[0] ? about[0]?.description : "";
    aboutTitle = about[0] ? about[0]?.title : `Welcome To ${locality}`;
  }

  const address = hotel.address || {};
  const hotelName = hotel.name;

  const imageUrl = useMemo(() => {
    if (hotel.gallery && hotel.gallery.length > 0) {
      const image = hotel.gallery[0];
      return image.startsWith("http") ? decodeURI(image) : `${baseUrl}${image}`;
    }
    return fallbackImage;
  }, [hotel.gallery]);

  const galleryImages = useMemo(
    () =>
      hotel?.gallery_images?.map((image) => ({
        imageUrl: decodeURI(
          image.url.indexOf("http") > -1 ? image.url : baseUrl + image.url
        ),
        altText: image.alt,
      })),
    [hotel?.gallery_images]
  );
  return (
    <Container isMobile={isMobile}>
      <Col lg={4} className={`image-wrapper`}>
        <HotelCarousel
          galleryImages={
            galleryImages?.length > 0
              ? galleryImages
              : [{ imageUrl, hotelName }]
          }
        />
      </Col>
      <Col className={`h-100 ms-lg-5 ${mobileMargin}`}>
        <Row className="h-100 px-0 pb-31 pt-0 pe-lg-0 g-0">
          <Col className="d-flex flex-column pe-0 pe-sm-3 pe-lg-0">
            <Title id="hotel-title">{hotel.name}</Title>
            <Address address={address} />

            {/* Mobile View */}
            {isMobile && (
              <AccordionDetails
                htmlContent={aboutText}
                hotel={hotel}
                amenities={hotel.amenities}
                aboutTitle={aboutTitle}
                checkInTime={checkInTime}
                checkOutTime={checkOutTime}
                phone={hotel?.phone}
              />
            )}

            {/* Desktop View */}
            {!isMobile && (
              <div className="mt-lg-3">
                <Row>
                  <Col md={6}>
                    <AboutTitle title={aboutTitle} />
                    <Time
                      checkInTime={checkInTime}
                      checkOutTime={checkOutTime}
                    />
                    <Phone phone={hotel?.phone} />
                  </Col>
                  {hotel.amenities && hotel.amenities.length > 0 && (
                    <Col md={6}>
                      <AboutTitle title={"Includes"} />
                      <AmenityList amenities={hotel.amenities} />
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col md={12}>
                    <AccordionDetails
                      htmlContent={aboutText}
                      hotel={hotel}
                      amenities={hotel.amenities}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </Col>
        </Row>
      </Col>
    </Container>
  );
};

export default HotelSummary;
